import { Box, Button, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import StickyNote2Rounded from '@mui/icons-material/StickyNote2Rounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import ChatRounded from '@mui/icons-material/ChatRounded';
import LibraryMusicRounded from '@mui/icons-material/LibraryMusicRounded';
import OndemandVideoRounded from '@mui/icons-material/OndemandVideoRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import WorkRounded from '@mui/icons-material/WorkRounded';
import PasswordRounded from '@mui/icons-material/PasswordRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { useAuthShell } from '../components/layout/useAuthShell';
import Home from './Home';

const float1 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-30px,24px) scale(1.12)}`;
const float2 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(36px,-20px) scale(.92)}`;
const bob = keyframes`0%,100%{transform:translateY(0) rotate(var(--r))}50%{transform:translateY(-12px) rotate(var(--r))}`;
const sheen = keyframes`0%{transform:translateX(-140%) skewX(-22deg)}22%,100%{transform:translateX(420%) skewX(-22deg)}`;

type Feature = {
  title: string;
  text: string;
  icon: React.ComponentType<any>;
  colors: [string, string];
  path: string;
};

// Same colours as the section tiles in the app and on mobile.
const FEATURES: Feature[] = [
  { title: 'Notes', text: 'Rich notes with categories, tags, reminders and files.', icon: StickyNote2Rounded, colors: ['#6226A7', '#B06AB3'], path: '/notes' },
  { title: 'Payments', text: 'Track income, expenses and transfers with receipts.', icon: PaymentsRounded, colors: ['#11998E', '#38EF7D'], path: '/payments' },
  { title: 'Grocery', text: 'A shopping list you can tick off as you go.', icon: ShoppingCartRounded, colors: ['#FF9800', '#E65100'], path: '/grocery' },
  { title: 'Chat', text: 'Message friends and share files in real time.', icon: ChatRounded, colors: ['#00B4DB', '#0083B0'], path: '/chat' },
  { title: 'Tunes', text: 'Upload music, build playlists, play anywhere.', icon: LibraryMusicRounded, colors: ['#4776E6', '#8E54E9'], path: '/tunes' },
  { title: 'Videos', text: 'Password-protected videos and shared links.', icon: OndemandVideoRounded, colors: ['#EB3349', '#F45C43'], path: '/videos' },
  { title: 'Documents', text: 'Folders and files, organised your way.', icon: FolderRounded, colors: ['#CF1E52', '#FF6B8B'], path: '/documents' },
  { title: 'Calendar', text: 'Every reminder and due payment in one view.', icon: CalendarMonthRounded, colors: ['#2563EB', '#60A5FA'], path: '/calendar' },
  { title: 'Reports', text: 'Trends and totals that make sense of it all.', icon: BarChartRounded, colors: ['#0F766E', '#2DD4BF'], path: '/reports' },
  { title: 'Projects', text: 'Tasks, timelines and time tracking for teams.', icon: WorkRounded, colors: ['#373B44', '#4286F4'], path: '/pma' },
  { title: 'Password generator', text: 'Create strong passwords in one click.', icon: PasswordRounded, colors: ['#6226A7', '#B06AB3'], path: '/password-generator' },
  { title: 'Speed test', text: 'Check your download, upload and latency.', icon: SpeedRounded, colors: ['#1E3A8A', '#2563EB'], path: '/internet-speed' },
];

const FeatureTile = ({ f, index, onOpen }: { f: Feature; index: number; onOpen: (path: string) => void }) => {
  const Icon = f.icon;
  return (
    <Box
      role="link"
      tabIndex={0}
      onClick={() => onOpen(f.path)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(f.path); } }}
      className="qs-animate-in"
      sx={{
        position: 'relative', overflow: 'hidden', cursor: 'pointer', color: '#fff', borderRadius: '24px', p: 2.25, minHeight: 150,
        background: `linear-gradient(135deg, ${f.colors[0]}, ${f.colors[1]})`,
        boxShadow: `0 14px 28px ${f.colors[1]}55, inset 0 0 0 1px rgba(255,255,255,.35)`,
        animationDelay: `${0.04 * index}s`,
        outline: 'none',
        transition: 'transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease',
        '&::before': {
          content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(135deg, rgba(255,255,255,.38) 0%, rgba(255,255,255,.08) 42%, rgba(255,255,255,0) 43%, rgba(0,0,0,.12) 100%)',
        },
        '&::after': {
          content: '""', position: 'absolute', top: 0, bottom: 0, left: 0, width: '30%', pointerEvents: 'none',
          background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.45), rgba(255,255,255,0))',
          transform: 'translateX(-140%) skewX(-22deg)',
        },
        '@media (prefers-reduced-motion: no-preference)': {
          '&::after': { animation: `${sheen} 8s ease-in-out ${index * 0.6}s infinite` },
          '&:hover': { transform: 'translateY(-6px) scale(1.02)', boxShadow: `0 22px 40px ${f.colors[1]}77, inset 0 0 0 1px rgba(255,255,255,.5)` },
        },
        '&:focus-visible': { boxShadow: `0 0 0 3px #fff, 0 0 0 6px ${f.colors[1]}` },
      }}
    >
      <Icon sx={{ position: 'absolute', right: -14, bottom: -20, fontSize: 120, color: 'rgba(255,255,255,.18)', transform: 'rotate(-12deg)' }} />
      <Stack sx={{ position: 'relative', height: '100%' }} justifyContent="space-between" spacing={1.5}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: '14px', display: 'grid', placeItems: 'center',
            background: 'linear-gradient(135deg, rgba(255,255,255,.45), rgba(255,255,255,.12))',
            border: '1px solid rgba(255,255,255,.5)',
          }}
        >
          <Icon />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.15, textShadow: '0 2px 8px rgba(0,0,0,.25)' }}>{f.title}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500, mt: 0.25 }}>{f.text}</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

/** Floating glossy chip used in the hero artwork. */
const FloatCard = ({
  title, sub, icon: Icon, colors, sx,
}: {
  title: string; sub: string; icon: React.ComponentType<any>; colors: [string, string]; sx: object;
}) => (
  <Box
    sx={{
      position: 'absolute', display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, borderRadius: '20px', color: '#fff',
      background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
      boxShadow: `0 18px 34px ${colors[1]}66, inset 0 0 0 1px rgba(255,255,255,.4)`,
      '@media (prefers-reduced-motion: no-preference)': { animation: `${bob} 6s ease-in-out infinite` },
      ...sx,
    }}
  >
    <Box sx={{ width: 40, height: 40, borderRadius: '13px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.28)' }}>
      <Icon />
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>{title}</Typography>
      <Typography variant="caption" sx={{ opacity: 0.95, fontWeight: 600 }}>{sub}</Typography>
    </Box>
  </Box>
);

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthed } = useAuthShell();

  // Anyone can browse the tiles; signed-out visitors are sent to log in first.
  const open = (path: string) => navigate(isAuthed ? path : '/login');
  const primaryLabel = isAuthed ? 'Go to dashboard' : 'Get started';
  const primaryPath = isAuthed ? '/dashboard' : '/login';

  return (
    <Box sx={{ width: '100%', maxWidth: 1536, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, md: 4 } }}>
      {/* ===== Hero ===== */}
      <Box
        className="qs-animate-in"
        sx={{
          position: 'relative', overflow: 'hidden', color: '#fff', borderRadius: '36px',
          p: { xs: 3, md: 6 }, minHeight: { md: 460 },
          display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' }, alignItems: 'center', gap: 3,
          background: 'linear-gradient(120deg, #2E0F5E 0%, #6226A7 32%, #B13FD6 62%, #F6A938 100%)',
          backgroundSize: '220% 220%',
          boxShadow: '0 30px 60px rgba(98,38,167,.38), inset 0 0 0 1px rgba(255,255,255,.25)',
          '@media (prefers-reduced-motion: no-preference)': { animation: 'qsGradientShift 14s ease infinite' },
        }}
      >
        <Box sx={{ position: 'absolute', width: 420, height: 420, right: -80, top: -140, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,214,122,.6), transparent 65%)', '@media (prefers-reduced-motion: no-preference)': { animation: `${float1} 12s ease-in-out infinite` } }} />
        <Box sx={{ position: 'absolute', width: 460, height: 460, left: -140, bottom: -240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,111,216,.5), transparent 65%)', '@media (prefers-reduced-motion: no-preference)': { animation: `${float2} 14s ease-in-out infinite` } }} />
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,.22) 0%, rgba(255,255,255,0) 40%)' }} />

        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              display: 'inline-block', px: 1.75, py: 0.6, borderRadius: '999px', mb: 2, fontWeight: 800, fontSize: 13, letterSpacing: 0.4,
              background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.35)', backdropFilter: 'blur(8px)',
            }}
          >
            ✨ Notes · Payments · Grocery · Chat · Tunes · Videos
          </Box>
          <Typography
            component="h1"
            sx={{ fontWeight: 900, fontSize: { xs: 36, md: 56 }, lineHeight: 1.05, letterSpacing: '-0.03em', textShadow: '0 6px 24px rgba(0,0,0,.25)' }}
          >
            Your whole life,{' '}
            <Box component="span" sx={{ background: 'linear-gradient(90deg,#FFE29A,#FFFFFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              beautifully organised.
            </Box>
          </Typography>
          <Typography sx={{ mt: 2, fontSize: { xs: 16, md: 19 }, maxWidth: 560, opacity: 0.95, lineHeight: 1.55 }}>
            QuillStack brings your notes, money, shopping lists, chats, music and videos into one fast, colourful workspace.
            On the web and on your phone.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3.5 }}>
            <Button
              onClick={() => navigate(primaryPath)}
              endIcon={<ArrowForwardRounded />}
              sx={{
                px: 3.5, py: 1.5, borderRadius: '999px', fontWeight: 900, fontSize: 16, color: '#4a1d86',
                background: 'linear-gradient(180deg,#fff,#EFE3FF)',
                boxShadow: '0 14px 28px rgba(0,0,0,.3), inset 0 1px 0 #fff',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              {primaryLabel}
            </Button>
            <Button
              onClick={() => navigate('/about')}
              sx={{
                px: 3.5, py: 1.5, borderRadius: '999px', fontWeight: 800, fontSize: 16, color: '#fff',
                border: '2px solid rgba(255,255,255,.75)',
                '&:hover': { bgcolor: 'rgba(255,255,255,.16)', transform: 'translateY(-2px)' },
              }}
            >
              Learn more
            </Button>
          </Stack>
        </Box>

        {/* floating artwork */}
        <Box sx={{ position: 'relative', height: { xs: 230, md: 340 }, display: { xs: 'none', sm: 'block' } }}>
          <FloatCard title="Payments" sub="Income & expense" icon={PaymentsRounded} colors={['#11998E', '#38EF7D']} sx={{ top: '4%', left: '4%', '--r': '-4deg', transform: 'rotate(-4deg)' }} />
          <FloatCard title="Notes" sub="Ideas & reminders" icon={StickyNote2Rounded} colors={['#6226A7', '#B06AB3']} sx={{ top: '34%', right: '2%', '--r': '3deg', transform: 'rotate(3deg)', animationDelay: '.8s' }} />
          <FloatCard title="Grocery" sub="Shopping list" icon={ShoppingCartRounded} colors={['#FF9800', '#E65100']} sx={{ bottom: '16%', left: '12%', '--r': '2deg', transform: 'rotate(2deg)', animationDelay: '1.6s' }} />
          <FloatCard title="Chat" sub="Messages" icon={ChatRounded} colors={['#00B4DB', '#0083B0']} sx={{ bottom: '0%', right: '8%', '--r': '-3deg', transform: 'rotate(-3deg)', animationDelay: '2.2s' }} />
        </Box>
      </Box>

      {/* ===== Utilities: Password generator + Net speed test (kept on the home page) ===== */}
      <Box id="password-generator" sx={{ mt: { xs: 3, md: 4 }, scrollMarginTop: 90 }}>
        <Home section="password" />
      </Box>
      <Box id="net-speed-test" sx={{ mt: { xs: 3, md: 4 }, scrollMarginTop: 90 }}>
        <Home section="speed" />
      </Box>

      {/* ===== Features ===== */}
      <Box sx={{ mt: { xs: 4, md: 6 }, mb: 2.5, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: 26, md: 34 }, letterSpacing: '-0.02em' }}>
          Everything you need, in one place
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Pick a section to jump straight in.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' } }}>
        {FEATURES.map((f, i) => (
          <FeatureTile key={f.title} f={f} index={i} onOpen={open} />
        ))}
      </Box>

      {/* ===== Closing call-to-action ===== */}
      <Box
        sx={{
          mt: { xs: 4, md: 6 }, position: 'relative', overflow: 'hidden', borderRadius: '30px', p: { xs: 3, md: 4.5 }, textAlign: 'center',
          bgcolor: 'background.paper', border: '1px solid rgba(98,38,167,.14)', boxShadow: '0 16px 40px rgba(98,38,167,.12)',
          '&::before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 6, background: 'linear-gradient(90deg,#6226A7,#EC4899,#F6A938)' },
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: { xs: 24, md: 32 }, letterSpacing: '-0.02em' }}>
          Ready to get organised?
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75, mb: 2.5 }}>
          Sign in and pick up where you left off.
        </Typography>
        <Button variant="contained" size="large" onClick={() => navigate(primaryPath)} endIcon={<ArrowForwardRounded />} sx={{ px: 4, py: 1.4, borderRadius: '999px' }}>
          {primaryLabel}
        </Button>
      </Box>
    </Box>
  );
}
